import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { wishlistService, authService } from '../services/api';
import { Plus, Link as LinkIcon, Share2, EyeOff, Trash, UserX } from 'lucide-react';
import { sanitizeUrl } from '../utils/sanitize';
import { useModal } from '../context/ModalContext';

const MyWishlist = () => {
  const modal = useModal();
  const [wishlist, setWishlist] = useState(null);
  const [itemName, setItemName] = useState('');
  const [itemLink, setItemLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [deletingChild, setDeletingChild] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const childId = searchParams.get('childId');

  const fetchWishlist = useCallback(async () => {
    try {
      const res = await wishlistService.getMine(childId);
      setWishlist(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      await wishlistService.addItem({ name: itemName, link: itemLink, childId });
      setItemName('');
      setItemLink('');
      fetchWishlist();
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error adding item', variant: 'danger' });
    }
  };

  const handleToggleShare = async () => {
    const nextShareState = !wishlist.isShared;

    if (childId && nextShareState) {
      const confirmMsg = `Sharing this wishlist will automatically make ${wishlist?.user?.name || "the child"} visible to members of all your family groups. Do you want to proceed?`;
      const confirmed = await modal.confirm({
        title: 'Share Wishlist',
        message: confirmMsg,
        variant: 'warning',
        confirmText: 'Proceed'
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      await wishlistService.toggleShare(nextShareState, childId);
      fetchWishlist();
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error updating share status', variant: 'danger' });
    }
  };

  const handleDeleteChild = async () => {
    const childName = wishlist?.user?.name || 'this child';
    const confirmMsg = `Are you sure you want to permanently delete ${childName} and their wishlist?`;
    const confirmed = await modal.confirm({
      title: 'Delete Child Account',
      message: confirmMsg,
      variant: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setDeletingChild(true);
    try {
      await authService.deleteChildAccount(childId);
      navigate('/dashboard');
    } catch (err) {
      modal.alert({ title: 'Error', message: err.response?.data?.msg || 'Failed to delete child account', variant: 'danger' });
      setDeletingChild(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (wishlist?.isShared) {
      const confirmMsg = "Are you sure you want to remove this item? This warning is shown for all shared wishlist items and does not indicate whether or not the gift has been purchased.";
      const confirmed = await modal.confirm({
        title: 'Remove Item',
        message: confirmMsg,
        variant: 'danger',
        confirmText: 'Remove'
      });
      if (!confirmed) {
        return;
      }
    }

    try {
      await wishlistService.deleteItem(itemId, childId);
      fetchWishlist();
    } catch (err) {
      modal.alert({ title: 'Error', message: 'Error removing item', variant: 'danger' });
    }
  };

  if (loading) return <div className="text-center mt-10">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto mt-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold text-xmas-red">
          {childId ? `Managing ${wishlist?.user?.name || "Child"}'s Wishlist` : 'My Wishlist'}
        </h1>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {childId && (
            <button
              onClick={handleDeleteChild}
              disabled={deletingChild}
              className="btn-secondary"
            >
              <UserX size={20} /> Delete Child Account
            </button>
          )}
          <button
            onClick={handleToggleShare}
            className={`btn-${wishlist?.isShared ? 'secondary' : 'primary'}`}
          >
            {wishlist?.isShared ? <><Share2 size={20}/> Shared with Family</> : <><EyeOff size={20}/> Private (Share Now)</>}
          </button>
        </div>
      </div>

      <div className="card mb-8">
        <h3 className="text-xl font-bold mb-4">Add New Item</h3>
        <form onSubmit={handleAddItem} className="space-y-4">
          <div>
            <label className="block text-sm font-medium">Gift Name</label>
            <input
              type="text"
              className="w-full p-2 border rounded mt-1"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Link (Optional)</label>
            <input
              type="url"
              className="w-full p-2 border rounded mt-1"
              placeholder="https://amazon.com/..."
              value={itemLink}
              onChange={(e) => setItemLink(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary w-full">
            <Plus size={20}/> Add to List
          </button>
        </form>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-bold mb-4">Current Items</h3>
        {wishlist?.items.length === 0 ? (
          <p className="text-gray-500 italic">No items added yet. Start adding gifts above!</p>
        ) : (
          wishlist?.items.map((item) => {
            const safeLink = sanitizeUrl(item.link);
            return (
              <div key={item.id} className="card py-4 flex justify-between items-center">
                <div>
                  <span className="font-bold text-lg">{item.name}</span>
                  {safeLink && (
                    <a href={safeLink} target="_blank" rel="noopener noreferrer" className="ml-4 text-blue-600 hover:underline flex items-center gap-1 inline-flex">
                      <LinkIcon size={14}/> View Link
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-xs text-gray-400 italic">
                      Bought status hidden from you
                  </div>
                  <button
                    onClick={() => handleDeleteItem(item.id)}
                    className="flex items-center gap-1 text-xs bg-xmas-red hover:bg-red-700 text-white font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline transition duration-150 ease-in-out"
                  >
                    <Trash size={12} /> Remove
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyWishlist;
